import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import locationsService from '../../services/locationsService';
import SearchableSelect from '../common/SearchableSelect';

const AddLocationModal = ({ isOpen, onClose, onSuccess, initialValues = {} }) => {
    const { t } = useTranslation();
    const locStrings = t('inquiriesModule.inquiriesForm.locationModal', { returnObjects: true });
    const [formData, setFormData] = useState({
        state: '',
        city: '',
        delivery_location: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            setFormData({
                state: initialValues.state || '',
                city: initialValues.city || '',
                delivery_location: initialValues.delivery_location || ''
            });
            setError(null);
            setValidationErrors({});
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const errors = {};
        if (!formData.state) errors.state = locStrings.validation.stateRequired;
        if (!formData.city) errors.city = locStrings.validation.cityRequired;
        if (!formData.delivery_location) errors.delivery_location = locStrings.validation.locationRequired;

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await locationsService.createLocation(formData);
            if (response.success) {
                onSuccess(response.data);
                onClose();
            } else {
                setError(response.error || locStrings.messages.error);
            }
        } catch (err) {
            setError(err.message || locStrings.messages.error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{locStrings.title}</h2>
                    <button onClick={onClose} disabled={loading} className="p-2 rounded-lg" style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="p-3 mb-4 text-sm rounded-xl" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1), color: UI_COLORS.STATUS.ERROR }}>
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{locStrings.fields.state.label} *</label>
                        <SearchableSelect
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            options={t('locations.states', { returnObjects: true })}
                            placeholder={locStrings.fields.state.placeholder}
                            error={validationErrors.state}
                        />
                        {validationErrors.state && (
                            <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{validationErrors.state}</div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{locStrings.fields.city.label} *</label>
                        <input 
                            name="city" 
                            value={formData.city} 
                            onChange={handleChange} 
                            className="w-full px-4 py-3 rounded-xl border-2" 
                            placeholder={locStrings.fields.city.placeholder} 
                            style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: validationErrors.city ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} 
                        />
                        {validationErrors.city && (
                            <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{validationErrors.city}</div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{locStrings.fields.deliveryLocation.label} *</label>
                        <textarea 
                            name="delivery_location" 
                            value={formData.delivery_location} 
                            onChange={handleChange} 
                            className="w-full px-4 py-3 rounded-xl border-2 resize-none h-24" 
                            placeholder={locStrings.fields.deliveryLocation.placeholder} 
                            style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: validationErrors.delivery_location ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} 
                        />
                        {validationErrors.delivery_location && (
                            <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{validationErrors.delivery_location}</div>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                        <button type="button" onClick={onClose} disabled={loading} className="px-5 py-2.5 rounded-xl font-medium" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>
                            {locStrings.buttons.cancel}
                        </button>
                        <button type="submit" disabled={loading} className="px-5 py-2.5 rounded-xl font-semibold flex items-center" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                            {loading ? <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> : <FontAwesomeIcon icon={faSave} className="mr-2" />}
                            {locStrings.buttons.save}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddLocationModal;
